import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchPortalsStatements } from '../../api/documents';
import { DocumentDetails } from './contractsSlice';

interface StatementsState {
  statements: DocumentDetails[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const CACHE_DURATION = 5 * 60 * 1000;

const initialState: StatementsState = {
  statements: [],
  loading: false,
  error: null,
  lastFetched: null,
};

export const fetchStatements = createAsyncThunk<
  DocumentDetails[],
  string,
  { state: { statements: StatementsState }; rejectValue: string }
>('statements/fetchStatements', async (kboNumber, { getState, rejectWithValue }) => {
  const { statements, lastFetched } = getState().statements;

  if (lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
    return statements;
  }

  try {
    const docs = await fetchPortalsStatements(kboNumber);
    return docs.map((doc) => ({
      documentCode: doc.file_name.split('-')[0],
      date: doc.date,
      documentName: doc.name,
      ean: doc.ean ?? undefined,
      fileName: doc.file_name,
      docType: 'Statement',
    }));
  } catch (err) {
    if (err instanceof Error) {
      return rejectWithValue(err.message);
    }
    return rejectWithValue('An unknown error occurred');
  }
});

const statementsSlice = createSlice({
  name: 'statements',
  initialState,
  reducers: {
    resetStatementsState: (state) => {
      state.statements = [];
      state.loading = false;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStatements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatements.fulfilled, (state, action: PayloadAction<DocumentDetails[]>) => {
        state.loading = false;
        state.statements = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchStatements.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'An unknown error occurred';
      });
  },
});

export const { resetStatementsState } = statementsSlice.actions;
export default statementsSlice.reducer;
